'use client';

import moment, { Moment } from "moment"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import * as echarts from 'echarts'

import { categoricalColorSchemes } from "@nivo/colors"
import { useDarkMode } from "@/hooks/useDarkMode";
import { useIsMobile } from "@/hooks/isMobile";
import { TrimContext } from "@/contexts/trimContext";

export type ColorSchemeKey = keyof typeof categoricalColorSchemes;

export interface ChartDataPoint {
  x: string;
  y: number;
}

export interface ChartSeries {
  id: string;
  data: ChartDataPoint[];
}

export interface TooltipConfig {
  xAxisFormatter?: (v: Date) => string;
  seriesNameFormatter?: (name: string) => string;
  seriesValueFormatter?: (value: number) => string;
}

export interface BarChartProps {
  // Required
  data: ChartSeries[];

  // Optional configuration
  title?: string;
  description?: string;
  height?: string;
  showLegend?: boolean;
  
  // Y-axis configuration
  yAxisFormatter?: (value: number) => string;
  yAxisMax?: number;
  yAxisScale?: boolean;
  
  // Tooltip configuration
  tooltip?: TooltipConfig;
  
  // Styling
  colorScheme?: ColorSchemeKey;
  stack?: boolean;
  stackOrder?: 'ascending' | 'descending' | 'insideOut' | 'none' | 'seriesDesc' | 'seriesAsc';
  
  // Bar-specific configurations
  barWidth?: string | number;
  barGap?: string | number;
  barCategoryGap?: string | number;
  showBackground?: boolean;
  borderRadius?: number | number[];

  trim?: Moment,
  showTotal?: boolean
}

const defaultProps: Partial<BarChartProps> = {
  height: 'h-full',
  showLegend: true,
  yAxisScale: true,
  colorScheme: 'tableau10',
  stack: true,
  stackOrder: 'seriesDesc',
  barWidth: 'auto',
  barGap: '20%',
  barCategoryGap: '20%',
  showBackground: false,
  borderRadius: 0,
};

export function BarChart({
  data,
  title,
  description,
  height = defaultProps.height,
  showLegend = defaultProps.showLegend,
  yAxisFormatter,
  yAxisMax,
  yAxisScale = defaultProps.yAxisScale,
  tooltip,
  colorScheme = defaultProps.colorScheme,
  stack = defaultProps.stack,
  stackOrder = defaultProps.stackOrder,
  barWidth = defaultProps.barWidth,
  barGap = defaultProps.barGap,
  barCategoryGap = defaultProps.barCategoryGap,
  showBackground = defaultProps.showBackground,
  borderRadius = defaultProps.borderRadius,
  showTotal,
}: BarChartProps) {
  const [hovered, setHovered] = useState<string | undefined>(undefined)
  const [chartInstance, setChartInstance] = useState<echarts.ECharts>()
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())
  const trim = useContext(TrimContext)
  const chartRef = useRef<HTMLDivElement>(null)
  const isDark = useDarkMode()
  const isMobile = useIsMobile()
 
  const colors = categoricalColorSchemes[colorScheme || 'tableau10'];
  const keys = useMemo(() => {
      const allKeys = [...new Set(data.map(s => s.id))]
  
      const presentKeys = allKeys.filter(
        s => {
          
          const sdata = data.find(d => d.id === s) || {data: []}
          
          const fdata = sdata.data.filter(p => {
            if (!trim) return true
            const _trim = moment.utc(trim).startOf('day')
            const px = moment.utc(p.x, ['YYYY-MM-DD'], true)

            return _trim.isSameOrBefore(px)
          })
            
          const bdata = fdata.reduce((a, v) => (a || !!v), false)
            
          return bdata
        }
      )
      
      return presentKeys
    }, [data, trim])

  const defaultTooltipFormatter = (
    params: any[]
  ) => {
    const date = params[0].axisValue;
    let formattedDate = tooltip?.xAxisFormatter?
      tooltip.xAxisFormatter(date)
      :moment.utc(date).format('YYYY-MM-DD');
    let result = `${formattedDate}<br/>`;
    let resultGrid = `<div class='grid grid-cols-2 auto-cols-fr text-xs'>`
    resultGrid += `<div class='col-span-2'><span>${formattedDate}</span></div>`
    
    for (let param of params) {
      const value = param.value[1] as number;
      const isHovered = param.seriesName === hovered;
      const style = isHovered ? 'font-weight: bold; font-size: 1.2em;' : '';
      const classes = isHovered? 'font-bold text-sm': '';
      
      const formattedName = tooltip?.seriesNameFormatter 
        ? tooltip.seriesNameFormatter(param.seriesName)
        : param.seriesName;
      const formattedValue = tooltip?.seriesValueFormatter
        ? tooltip.seriesValueFormatter(value)
        : value.toString();
      
      result += `<span style="margin-right: 3em; ${style}">${param.marker} ${formattedName}</span><strong style="float:right; ${style}">${formattedValue}</strong><br/>`;
      resultGrid += `<div class="${classes}">${param.marker} ${formattedName}</div><div class='text-right ${classes}' style='${style}'>${formattedValue}</div>`
    }

    if (showTotal) {
      const to_add = params.map((d: any, idx: number) => {
        if (hiddenSeries.has(d.seriesName)) return 0
        else return d.data[1]
      })
      const total = to_add.reduce((acc: number, v?: string) => acc + (Number(v) || 0), 0) as number
      const formattedTotal = tooltip?.seriesValueFormatter
          ? tooltip.seriesValueFormatter(total)
          : total.toString();
      result += `<span style="margin-right: 3em; font-weight: bold">Total</span><strong style="float:right; font-weight: bold">${formattedTotal}</strong><br/>`;
      resultGrid += `<div class="font-bold">Total</div><div class='text-right font-bold'>${formattedTotal}</div>`
    }
    
    return resultGrid;
  };

  useEffect(() => {
    const element = chartRef.current;
    if (!element) return;

    // Ensure element has dimensions before initializing
    const initChart = () => {
      const _chartInstance = echarts.init(element);
      setChartInstance(_chartInstance);

      // Handle window resize
      const handleResize = () => {
        _chartInstance.resize();
      };

      // Handle container resize using ResizeObserver
      const resizeObserver = new ResizeObserver(() => {
        _chartInstance.resize();
      });
      resizeObserver.observe(element);

      // Handle series hover
      const handleMouseOver = (params: any) => {
        if (params.componentType === 'series') {
          setHovered(params.seriesName);
        }
      };

      const handleMouseOut = () => {
        setHovered(undefined);
      };

      window.addEventListener('resize', handleResize);
      _chartInstance.on('mouseover', handleMouseOver);
      _chartInstance.on('globalout', handleMouseOut);

      return () => {
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
        _chartInstance.off('mouseover', handleMouseOver);
        _chartInstance.off('globalout', handleMouseOut);
        _chartInstance.dispose();
      };
    };

    // Start initialization
    const cleanup = initChart();

    return () => {
      cleanup?.();
    };
  }, [])

  useEffect(() => {
    if (!keys || keys.length === 0) return;
    const textColor = isDark?"#ccc":"#222"

    const yAxisConfig: any = {
      type: 'value',
      scale: yAxisScale,
      axisLabel: {
        formatter: yAxisFormatter ? (n: any) => yAxisFormatter(n) : undefined,
        color: textColor
      },
      axisLine: {
        lineStyle: {
          color: textColor
        }
      },
      axisTick: {
        lineStyle: {
          color: textColor
        }
      }
    };

    if (yAxisMax !== undefined) {
      yAxisConfig.max = yAxisMax;
    }

    const dates = [
      ...new Set(
        data.map(s => s.data.map(p => p.x))
        .flat()
      )
    ].sort().filter(m => 
        trim?
          moment.utc(m).isSameOrAfter(moment(trim), 'month')
          : true
    )

    chartInstance?.setOption({
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        name: 'Date',
        data: dates,
        axisLabel: {
          color: textColor,
          formatter: (v: string) => {
            const date = moment.utc(v)
            if (date.month() === 0) {
              return date.format('YYYY')
            } else {
              return date.format('Do MMM')
            }
          }
        },
        axisLine: {
          lineStyle: {color: textColor}
        },
        axisTick: {
          lineStyle: {color: textColor}
        }
      },
      yAxis: yAxisConfig,
      series: keys.sort().map((key, idx) => ({
        type: 'bar',
        data: 
          hiddenSeries.has(key)?
            []:
            data
              .filter(s => s.id === key)
              .at(0)?.data
              .map(d => [d.x, d.y])
              .filter(([x, y]) => !trim || moment(x).isSameOrAfter(moment(trim).startOf('day'))),
        name: key,
        itemStyle: {
          opacity: hiddenSeries.has(key) ? 0 : (hovered ? (hovered === key ? 1 : 0.5) : 1),
          borderRadius: borderRadius
        },
        stack: stack,
        stackOrder: stack ? stackOrder : undefined,
        barWidth: barWidth,
        barGap: barGap,
        barCategoryGap: barCategoryGap,
        showBackground: showBackground,
        color: colors[idx % colors.length],
        triggerLineEvent: true,
        triggerAreaEvent: true,
      })),
      tooltip: {
        trigger: 'axis',
        formatter: defaultTooltipFormatter,
        className: 'text-fg bg-background xs:text-xs',
        backgroundColor: '',
        textStyle: {
          color: '',
        },
      },
      legend: {
        show: false
      }
    })
  }, [isDark, chartInstance, data, hovered, title, yAxisFormatter, yAxisMax, yAxisScale, tooltip, stack, stackOrder, barWidth, barGap, barCategoryGap, showBackground, borderRadius, colors, trim, hiddenSeries, keys])

  const handleLegendClick = (key: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    setHiddenSeries(prev => {
      // If all series are hidden, show all; otherwise hide all
      if (prev.size === keys.length) {
        return new Set();
      } else {
        return new Set(keys);
      }
    });
  };

  const textColor = isDark?"#ccc":"#222"

  return (
    <div className='flex flex-col h-full lg:flex-row'>
      <div className='flex flex-col grow'> 
        { /* title */ }
        <div className="flex flex-row">
          <span className="block text-2xl lg:ml-8 ml-4 grow">{title}</span>
          <span className="block text-xs place-self-end">{description}</span>
        </div>
        { /* chart */}
        <div className='w-full h-full' ref={chartRef} />
      </div>
      {/* legend */}
      {showLegend && (
        <div className='flex flex-col'>
        
          <div className={`flex hover:bg-primary/10 bg-muted rounded ${isMobile ? 'flex-row w-full' : 'flex-col w-[10vw] h-full ml-4'} gap-2 p-2 overflow-scroll`}>
            <button
              onClick={handleToggleAll}
              className={`flex gap-2 px-2 py-1 rounded text-sm transition-opacity font-medium ${
                hiddenSeries.size === keys.length ? 'opacity-100' : 'opacity-100'
              }`}
              style={{ color: textColor }}
            >
              <span className="truncate">
                {hiddenSeries.size === keys.length ? 'Show All' : 'Hide All'}
              </span>
            </button>
            {keys.sort().map((key, idx) => {
              const isHidden = hiddenSeries.has(key);
              return (
                <button
                  key={key}
                  onClick={() => handleLegendClick(key)}
                  onMouseEnter={() => setHovered(key)}
                  onMouseLeave={() => setHovered(undefined)}
                  className={`flex items-center gap-2 px-2 py-1 rounded text-sm transition-opacity ${
                    isHidden ? 'opacity-40' : 'opacity-100'
                  } ${hovered === key ? 'bg-primary/10' : ''}`}
                  style={{ color: textColor }}
                >
                  <span
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: colors[idx % colors.length] }}
                  />
                  <span className="truncate">{key.trim()}</span>
                </button>
              );
            })}
          </div>
      </div>)}

    </div>)
}
